// src/lib/guided/config.ts
//
// Phase 1 of docs/guided-shopping_plan.md: the owner's answers, as data.
// Everything else in the guided-selling feature (the compiler, the grade,
// the dashboard) derives from `FunnelConfig`.
import { z } from "zod";

/**
 * The owner-facing product name (Ben, 2026-09-23). Kept in one constant so a
 * rename stays a one-line change; the dashboard treats it as a label only
 * (CLAUDE.md §0a) and never surfaces "funnel", "compile" or "criteria".
 */
export const GUIDED_PRODUCT_NAME = "Kiroku Sales Lead Intelligence";

/**
 * The sourced criteria library (docs/sales-methodologies_research.md §5.2),
 * grouped by the plan's task 1.1. `gate`/`authorityGate` are hard/soft
 * qualifiers, the urgency family covers timing signals, `hesitation` maps to
 * the owner's written question answers (1.4), and `money` is always late and
 * optional.
 */
export const CRITERION_TYPES = [
  "gate",
  "fit",
  "authorityGate",
  "timing",
  "criticalEvent",
  "trigger",
  "stage",
  "severity",
  "priorAttempts",
  "priority",
  "hesitation",
  "outcomePriority",
  "money",
] as const;

export type CriterionType = (typeof CRITERION_TYPES)[number];

export const OFFER_ACTION_TYPES = ["book", "quote", "guide", "refer"] as const;
export type OfferActionType = (typeof OFFER_ACTION_TYPES)[number];

export const BucketSchema = z.object({
  id: z.string().min(1, "Each answer option needs an id"),
  label: z.string().min(1, "Each answer option needs a label"),
});
export type FunnelBucket = z.infer<typeof BucketSchema>;

export const OfferActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("book"),
    url: z
      .string()
      .min(1, "Enter a booking URL")
      .url("Enter a valid booking URL"),
  }),
  z.object({
    type: z.literal("quote"),
  }),
  z.object({
    type: z.literal("guide"),
    url: z.string().min(1, "Enter a guide URL").url("Enter a valid guide URL"),
  }),
  z.object({
    type: z.literal("refer"),
    url: z
      .string()
      .min(1, "Enter a referral URL")
      .url("Enter a valid referral URL"),
    text: z.string().min(1, "Enter the referral text"),
  }),
]);
export type FunnelOfferAction = z.infer<typeof OfferActionSchema>;

export const ConsequenceSchema = z.object({
  text: z.string().min(1, "Enter what happens if this waits"),
  source: z.string().min(1, "Enter a source credit for this claim"),
});
export type FunnelConsequence = z.infer<typeof ConsequenceSchema>;

export const OfferSchema = z.object({
  id: z.string().min(1, "Each offer needs an id"),
  name: z.string().min(1, "Each offer needs a name"),
  description: z.string().min(1, "Each offer needs a one-line description"),
  action: OfferActionSchema,
  /**
   * Challenger's "teach" (research §3.5 item 2, §3.4 table): one owner-
   * written, counter-intuitive reframe of this problem, shown on the result
   * screen (plan 5.1). Optional, genuinely -- "a missing reframe drops the
   * block; it is never generated" (research §3.4). No `.default()`: every
   * offer literal in templates.ts/presets.ts is typed as `FunnelOffer`
   * directly, so a `.default()` would make this required in `z.infer`'s
   * output type and fail `npm run type-check` on all of them (the same
   * `topBuckets`/`concerns` trap grade.ts and lints.ts already document).
   */
  reframe: z.string().min(1).optional(),
  /**
   * SPIN's implication ("left alone, [problem] usually leads to
   * [consequence]"), shown only when the owner also credits a source
   * (research §3.4/§3.5 item 2: "a consequence, only with a source"). The
   * source requirement lives inside this object rather than as a separate
   * optional field so the two can never be half-set: an owner who has not
   * supplied a source has not supplied a consequence, and the block simply
   * does not render (src/lib/guided/resultScreen.ts).
   */
  consequence: ConsequenceSchema.optional(),
});
export type FunnelOffer = z.infer<typeof OfferSchema>;

/**
 * UK Equality Act 2010 protected characteristics (age, disability,
 * pregnancy, religion, ethnicity, sexual orientation, gender) and their
 * common proxies, refused as fit criteria per plan 1.2 and
 * `docs/guided-shopping_research.md` §3a.5: using one to route or grade a
 * lead is unlawful discrimination in services, not a data-quality problem.
 *
 * A word list with stems, not a classifier: that is acceptable here because
 * it backs a human-readable refusal message, not a decision about a person.
 * Stems are chosen so they cannot collide with ordinary business vocabulary
 * — "age" is matched as a whole word only, never a prefix, so it does not
 * catch "average" or "agent"; "sexual" is a prefix so it also catches
 * "sexual orientation".
 */
const PROTECTED_CHARACTERISTIC_TERMS: { term: string; wholeWord: boolean }[] = [
  // age, and its common non-numeric proxies
  { term: "age", wholeWord: true },
  { term: "elderly", wholeWord: true },
  { term: "retire", wholeWord: false }, // retired, retirement, retiree
  { term: "pension", wholeWord: false }, // pension, pensioner
  // disability
  { term: "disab", wholeWord: false }, // disability, disabled, disabling
  { term: "wheelchair", wholeWord: true },
  { term: "impair", wholeWord: false }, // impairment, impaired
  { term: "handicap", wholeWord: false },
  // pregnancy and maternity
  { term: "pregnan", wholeWord: false }, // pregnant, pregnancy
  { term: "matern", wholeWord: false }, // maternity, maternal
  // religion or belief
  { term: "religio", wholeWord: false }, // religion, religious
  // race and ethnicity
  { term: "ethnic", wholeWord: false }, // ethnic, ethnicity
  { term: "racial", wholeWord: false },
  { term: "race", wholeWord: true },
  // sexual orientation
  { term: "sexual", wholeWord: false }, // also catches "sexual orientation"
  { term: "homosex", wholeWord: false },
  { term: "heterosex", wholeWord: false },
  { term: "bisex", wholeWord: false },
  { term: "gay", wholeWord: true },
  { term: "lesbian", wholeWord: true },
  { term: "lgbt", wholeWord: false }, // lgbt, lgbtq, lgbtqia
  // gender (and gender reassignment)
  { term: "gender", wholeWord: false }, // gender, genders, gendered
  { term: "transgend", wholeWord: false },
];

/** "over 60", "under 18", "above 65": numeric age proxies (plan 1.2). */
const AGE_NUMBER_PATTERN = /\b(?:over|under|above|below)\s*-?\s*\d{1,3}\b/i;

/**
 * Returns the matched term/phrase if `text` names a protected
 * characteristic or a common proxy for one, otherwise null.
 */
export function findProtectedCharacteristicMatch(text: string): string | null {
  const ageMatch = text.match(AGE_NUMBER_PATTERN);
  if (ageMatch) return ageMatch[0];
  for (const { term, wholeWord } of PROTECTED_CHARACTERISTIC_TERMS) {
    const pattern = new RegExp(wholeWord ? `\\b${term}\\b` : `\\b${term}`, "i");
    if (pattern.test(text)) return term;
  }
  return null;
}

export const CriterionSchema = z
  .object({
    id: z.string().min(1, "Each question needs an id"),
    question: z.string().min(1, "Each question needs its text"),
    buckets: z
      .array(BucketSchema)
      .min(2, "Each question needs at least 2 answer options")
      .max(5, "Each question can have at most 5 answer options"),
    source: z
      .string()
      .min(
        1,
        'Each question needs a source credit (for example "SPICED Critical Event")',
      ),
    type: z.enum(CRITERION_TYPES, {
      errorMap: () => ({
        message: `type must be one of: ${CRITERION_TYPES.join(", ")}`,
      }),
    }),
    /**
     * Plan 3.1: buckets that count as a "top" urgency signal for grade A,
     * read only when `type` is in the urgency family
     * (`timing`/`criticalEvent`/`trigger`/`stage`). Bucket order carries no
     * universal urgency meaning across those types -- `timing` and `trigger`
     * buckets read most-urgent-first in every shipped preset, `stage`
     * buckets read least-committed-first -- so `grade.ts` has no positional
     * default here; a preset or the owner opts a bucket in explicitly.
     *
     * Genuinely optional (no `.default([])`), deliberately: every existing
     * criterion literal across `presets.ts`/`templates.ts` is typed as
     * `FunnelCriterion` directly rather than parsed, so a `.default()` here
     * would make the field required in `z.infer`'s output type and fail
     * `npm run type-check` on every one of them (the same trap 2.9's
     * `concerns` hit, but that field lives on `FunnelConfigSchema`, built
     * only in a handful of test fixtures `tsconfig.json` does not check --
     * `CriterionSchema` literals are everywhere and are checked). `grade.ts`
     * reads `criterion.topBuckets ?? []`.
     */
    topBuckets: z.array(z.string()).optional(),
    /**
     * Plan 3.1: buckets that count as below the owner's floor for a grade
     * demotion, read only when `type` is "money". Money bands are always
     * authored ascending from the floor (research §3a.2: "Under [floor] /
     * [floor]-2x / Over 2x / Not sure yet"), so `grade.ts` falls back to the
     * first bucket when this is empty or absent; set it explicitly to
     * override. Genuinely optional, same reasoning as `topBuckets` above.
     */
    belowFloorBuckets: z.array(z.string()).optional(),
  })
  .superRefine((criterion, ctx) => {
    const questionMatch = findProtectedCharacteristicMatch(criterion.question);
    if (questionMatch) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["question"],
        message: `Criteria cannot name a protected characteristic or its proxy (matched "${questionMatch}")`,
      });
    }
    criterion.buckets.forEach((bucket, index) => {
      const bucketMatch = findProtectedCharacteristicMatch(bucket.label);
      if (bucketMatch) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buckets", index, "label"],
          message: `Buckets cannot name a protected characteristic or its proxy (matched "${bucketMatch}")`,
        });
      }
    });
    const bucketIds = new Set(criterion.buckets.map((bucket) => bucket.id));
    const checkBucketRefs = (field: "topBuckets" | "belowFloorBuckets") => {
      (criterion[field] ?? []).forEach((id, index) => {
        if (!bucketIds.has(id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field, index],
            message: `"${id}" is not one of this question's answer ids`,
          });
        }
      });
    };
    checkBucketRefs("topBuckets");
    checkBucketRefs("belowFloorBuckets");
  });
export type FunnelCriterion = z.infer<typeof CriterionSchema>;

/**
 * Which buckets, per criterion, make an offer a fit. `offerFit["consulting"]`
 * = `{ budget: ["over-2x", "1x-2x"] }` means the consulting offer accepts
 * either of those two `budget` buckets; a criterion absent from an offer's
 * entry is treated as not affecting that offer's fit (Phase 2 compiler
 * concern, not validated further here).
 */
export const OfferFitSchema = z.record(
  z.string(),
  z.record(z.string(), z.array(z.string())),
);
export type FunnelOfferFit = z.infer<typeof OfferFitSchema>;

export const NotFitSchema = z.object({
  text: z.string().min(1, "The polite-no message is required"),
  resourceUrl: z.string().url("Enter a valid resource URL").optional(),
});
export type FunnelNotFit = z.infer<typeof NotFitSchema>;

/**
 * Questions and concerns the owner answers once (plan 1.4, research §6.4):
 * a visitor can raise "what does it cost?" or "is my data safe?" from any
 * step, tap the matching button, and see the owner's own answer inline
 * without leaving the step. `examplePhrasings` is unused until a future
 * free-text box (research §4.4) exists to match against them.
 */
export const CONCERN_ACTION_TYPES = ["book", "pricing", "person"] as const;
export type ConcernActionType = (typeof CONCERN_ACTION_TYPES)[number];

export const ConcernActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("book"),
    url: z
      .string()
      .min(1, "Enter a booking URL")
      .url("Enter a valid booking URL"),
  }),
  z.object({
    type: z.literal("pricing"),
    url: z
      .string()
      .min(1, "Enter a pricing page URL")
      .url("Enter a valid pricing page URL"),
  }),
  z.object({
    type: z.literal("person"),
    url: z
      .string()
      .min(1, "Enter a contact URL")
      .url("Enter a valid contact URL"),
  }),
]);
export type FunnelConcernAction = z.infer<typeof ConcernActionSchema>;

export const MAX_CONCERNS = 6;
export const MAX_CONCERN_ANSWER_LENGTH = 400;

export const ConcernSchema = z.object({
  id: z.string().min(1, "Each question needs an id"),
  label: z.string().min(1, "Each question needs a button label"),
  answer: z
    .string()
    .min(1, "Each question needs an answer")
    .max(
      MAX_CONCERN_ANSWER_LENGTH,
      `Answers can be at most ${MAX_CONCERN_ANSWER_LENGTH} characters`,
    ),
  examplePhrasings: z.array(z.string().min(1)).optional().default([]),
  action: ConcernActionSchema.optional(),
});
export type FunnelConcern = z.infer<typeof ConcernSchema>;

/**
 * Plan 5.3, research §2.6 ("buyers dislike bots" -> "a visible way to reach
 * a person") and §2.4 (Gartner, n=5,728: 64% would rather companies did not
 * use AI for service, top fear is losing access to a person). Both fields
 * are independently optional -- an owner who wants neither shown simply
 * never sets this object, and `ownerContact.ts`'s `ownerContactLink` treats
 * an absent value the same as an absent config: nothing renders, never a
 * broken "call us" with no number attached.
 */
export const OwnerContactSchema = z.object({
  phone: z.string().min(1, "Enter a phone number").optional(),
  email: z
    .string()
    .min(1, "Enter an email address")
    .email("Enter a valid email address")
    .optional(),
});
export type FunnelOwnerContact = z.infer<typeof OwnerContactSchema>;

export const MIN_OFFERS = 1;
export const MAX_OFFERS = 10;
export const MIN_CRITERIA = 1;
export const MAX_CRITERIA = 8;
export const DEFAULT_MAX_QUESTIONS = 5;
export const HARD_CAP_MAX_QUESTIONS = 7;

export const FunnelConfigSchema = z.object({
  offers: z
    .array(OfferSchema)
    .min(MIN_OFFERS, "Add at least one offer")
    .max(MAX_OFFERS, "Add at most 10 offers"),
  criteria: z
    .array(CriterionSchema)
    .min(MIN_CRITERIA, "Add at least one question")
    .max(MAX_CRITERIA, "Add at most 8 questions"),
  offerFit: OfferFitSchema.optional().default({}),
  required: z.array(z.string().min(1)).optional().default([]),
  concerns: z
    .array(ConcernSchema)
    .max(MAX_CONCERNS, `Add at most ${MAX_CONCERNS} questions and concerns`)
    .optional()
    .default([]),
  maxQuestions: z
    .number()
    .int("maxQuestions must be a whole number")
    .min(1, "maxQuestions must be at least 1")
    .max(
      HARD_CAP_MAX_QUESTIONS,
      `maxQuestions cannot exceed ${HARD_CAP_MAX_QUESTIONS}`,
    )
    .optional()
    .default(DEFAULT_MAX_QUESTIONS),
  notFit: NotFitSchema,
  ownerContact: OwnerContactSchema.optional(),
  /**
   * Plan 5.7, research §2.6 item 4 ("consensus creation", Gartner's B2B
   * buying jobs): offers "Email me this summary" on the result screen.
   * Genuinely optional and undefined by default -- no `.default()`, the same
   * `topBuckets`/`consequence` reasoning documented above `OfferSchema`,
   * because a `.default()` here would make every `FunnelConfig` literal
   * across the codebase (every template, every test fixture) fail
   * `npm run type-check` for omitting a field they have no reason to set.
   * A trait carried by the config's own data, not a hardcoded check against
   * "is this the b2b-software-demo template": `FunnelConfig` never records
   * which seed it was loaded from (`FunnelSetup.tsx`'s `getTemplate(id)`
   * just `structuredClone`s the seed's config), so identity was never
   * available to check in the first place. Set `true` only on the
   * `b2b-software-demo` seed in templates.ts.
   */
  shareSummary: z.boolean().optional(),
});

export type FunnelConfig = z.infer<typeof FunnelConfigSchema>;

/** Convenience wrapper: parse an unknown value, returning zod's own result. */
export function parseFunnelConfig(value: unknown) {
  return FunnelConfigSchema.safeParse(value);
}

/**
 * Cheap presence check for "does this form have a guided-shopping funnel",
 * used to decide whether a dashboard surface shows guided-selling UI (the
 * grade column, plan 3.7) -- never full schema validation, so a form mid-edit
 * with a stale funnel key still shows the column rather than flickering it off.
 */
export function hasGuidedFunnel(
  settings: { funnel?: unknown } | null | undefined,
): boolean {
  return settings != null && settings.funnel != null;
}

/** Coerces to a plain record the same way forms.ts's private settingsObject() does: anything but a plain object is "no settings". */
function asSettingsRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Plan 6.1: does this write newly count against the plan's `guidedFlows`
 * limit? True only on the transition into "active form with a funnel" -- an
 * edit to an already-published flow, or a save that leaves it alone, must
 * never re-check the limit (research §2.6's "editing after launch"
 * complaint). `updateForm` (src/lib/database/forms.ts) shallow-merges
 * `settings` onto the existing row rather than replacing it, so `after`'s
 * settings here are merged the same way: only the keys it carries override
 * the row's own, and an absent `after.settings` (a save that never mentions
 * settings) leaves the row's funnel exactly as it was.
 */
export function willNewlyPublishGuidedFlow(
  before: { active: boolean; settings: unknown } | null,
  after: { active?: boolean; settings?: unknown },
): boolean {
  const beforeSettings = before ? asSettingsRecord(before.settings) : {};
  const wasPublished = !!before?.active && hasGuidedFunnel(beforeSettings);
  if (wasPublished) return false;

  const nextActive =
    after.active !== undefined ? after.active : (before?.active ?? true);
  const nextSettings =
    after.settings !== undefined
      ? { ...beforeSettings, ...asSettingsRecord(after.settings) }
      : beforeSettings;

  return nextActive === true && hasGuidedFunnel(nextSettings);
}
